import * as Sentry from "@sentry/browser";
import { produce } from "immer";

import { initialAgenticAiState } from "./agenticAiState";
import type { AgenticAiAction, AgenticAiState } from "./types";

/**
 * Groups a duplicate-registration report by what duplicated rather than by the message Sentry
 * would otherwise hash.
 *
 * The default grouping keys on the message and the stack, both of which are the same
 * `captureException` call for every surface, so one page's duplicates and another's land close
 * enough together to be merged into a single issue by hand - and then a fix for one surface
 * cannot be resolved without dismissing the rest. Interpolated values make the opposite problem:
 * a per-mount `toolEntityId` fragments one defect across an issue per button. Naming the kind and
 * the registrant, and nothing else, gives one issue per thing that can duplicate.
 *
 * @param kind Which registry rejected the duplicate
 * @param registrant The tool name, context identifier, or skill name that was already taken
 * @returns The Sentry fingerprint for this report
 */
const duplicateRegistrationFingerprint = (kind: string, registrant: string): string[] => [
  "agenticAi",
  "duplicate-registration",
  kind,
  registrant,
];

/**
 * Whether a duplicate registration is between global registrations only, which is logged to the
 * console but not reported to Sentry.
 *
 * Global tools and contexts (`ignoreReactAppRootState`) are live in both the React SPA and the
 * React islands embedded in Knockout pages, and the Knockout <> React interop sometimes registers
 * them twice. Those reports carry
 * no signal we act on, while the same report for a local registration usually points at a real
 * defect. A collision between a global and a local registration is still reported.
 *
 * Once the Knockout to React migration is finished, `ignoreReactAppRootState` and all the logic
 * built on it can be removed, and every duplicate, local or global, is reported to Sentry again.
 *
 * @param incoming The `ignoreReactAppRootState` flag of the registration being added
 * @param existing The `ignoreReactAppRootState` flags of the registrations it collides with
 * @returns Whether every side of the collision is global
 */
const isGlobalDuplicate = (incoming: boolean | undefined, existing: (boolean | undefined)[]): boolean =>
  incoming === true && existing.every((flag) => flag === true);

/**
 * Unions brand guideline IDs into the pending set, which the chat UI consumes
 * (and clears) once it has attached them.
 *
 * @param state The draft state to update
 * @param ids The IDs to queue
 */
const queueBrandGuidelineContextIds = (state: AgenticAiState, ids: readonly string[]): void => {
  state.pendingBrandGuidelineContextIds = [...new Set([...(state.pendingBrandGuidelineContextIds ?? []), ...ids])];
};

export const agenticAiReducer = (
  prevState: AgenticAiState | undefined = initialAgenticAiState,
  action: AgenticAiAction
): AgenticAiState => {
  return produce(prevState, (state) => {
    switch (action.type) {
      case "agenticAi/setSessionId":
        state.sessionId = action.sessionId;
        state.loadedSkillNames = [];
        break;
      case "agenticAi/openChat":
        state.isOpen = true;
        state.supportEntryPoint = action.withSupportEntryPoint ?? false;
        state.pendingExistingChatResponse = action.existingChatResponse;
        state.dynamicPromptsContext = action.dynamicPromptsContext;
        // Unlike dynamicPromptsContext, pending IDs are not cleared when absent:
        // they are consumed (and cleared) by the chat UI within moments, and an
        // unrelated concurrent open must not drop an in-flight attachment.
        if (action.brandGuidelineContextIds?.length) {
          queueBrandGuidelineContextIds(state, action.brandGuidelineContextIds);
        }
        break;
      case "agenticAi/attachBrandGuidelineContexts":
        // A closed chat takes nothing in, so a tool call that found it closed
        // never has its guidelines land as chips after reporting them missing.
        // openChat is how to attach to a closed chat.
        if (state.isOpen && action.brandGuidelineContextIds.length > 0) {
          queueBrandGuidelineContextIds(state, action.brandGuidelineContextIds);
        }
        break;
      case "agenticAi/closeChat":
        state.isOpen = false;
        // Reset the support entry point flag when the chat is closed;
        // this allows users to get dynamic prompts again, or to re-enable the flag by
        // opening the chat from the support menu again.
        state.supportEntryPoint = false;
        state.pendingExistingChatResponse = undefined;
        state.dynamicPromptsContext = undefined;
        state.pendingBrandGuidelineContextIds = undefined;
        break;
      case "agenticAi/registerTool": {
        if (action.key in state.frontendTools) {
          throw new Error(`Tool ${action.key} already registered`);
        }

        const { name, toolEntityId } = action.tool.definition;

        if (name in state.frontendToolNames) {
          // Which existing registrations this one actually conflicts with: every same-named
          // one when there is no toolEntityId to disambiguate by, otherwise only those
          // claiming the same entity.
          const existingKeys = state.frontendToolNames[name];
          const conflictingKeys = toolEntityId
            ? existingKeys.filter((key) => state.frontendTools[key]?.definition.toolEntityId === toolEntityId)
            : existingKeys;

          // A conflict between two gated registrations from opposite React trees is the
          // route handoff, not a defect: each tree derives its own
          // `isCorrectReactAppRootState` from `#react-root`'s `data-enabled` through its
          // own MutationObserver, and each commits and flushes passive effects on its own
          // schedule, so the acquiring tree can register before the releasing tree's
          // cleanup runs. It reconciles within a tick, and only the global navigation
          // chrome renders in both trees at once.
          //
          // Both sides must be stamped for this to hold - an unstamped registration
          // bypassed the gate (`ignoreReactAppRootState`) or the provider entirely, so
          // nothing keeps it exclusive and its duplicates are not a handoff (see
          // `isGlobalDuplicate` for which of those reach Sentry). A conflict within one
          // tree is two components claiming one identity, and is likewise reported.
          const { originIsWithinReactRoot } = action.tool;
          const isReactRootHandoff =
            originIsWithinReactRoot !== undefined &&
            conflictingKeys.every((key) => {
              const existingOrigin = state.frontendTools[key]?.originIsWithinReactRoot;
              return existingOrigin !== undefined && existingOrigin !== originIsWithinReactRoot;
            });

          if (conflictingKeys.length > 0 && !isReactRootHandoff) {
            // We only allow duplicate tool registration when toolEntityId is provided to avoid ambiguity.
            const error = toolEntityId
              ? new Error(`Tool ${name} with toolEntityId ${toolEntityId} already registered`)
              : new Error(`Tool ${name} already registered`);
            console.error(error);
            const existingFlags = conflictingKeys.map((key) => state.frontendTools[key]?.ignoreReactAppRootState);
            if (!isGlobalDuplicate(action.tool.ignoreReactAppRootState, existingFlags)) {
              Sentry.captureException(error, {
                level: "error",
                // Fingerprinted on the tool name alone: `toolEntityId` is generated per mount for
                // the Braze* wrappers, so including it opens a new issue for every element.
                fingerprint: duplicateRegistrationFingerprint(toolEntityId ? "tool-entity" : "tool", name),
              });
            }
          }
          state.frontendToolNames[name].push(action.key);
        } else {
          state.frontendToolNames[name] = [action.key];
        }

        state.frontendTools[action.key] = action.tool;
        break;
      }
      case "agenticAi/unregisterTool":
        if (state.frontendTools[action.key]) {
          const name = state.frontendTools[action.key].definition.name;
          delete state.frontendTools[action.key];

          state.frontendToolNames[name] = state.frontendToolNames[name].filter((key) => key !== action.key);
          if (state.frontendToolNames[name].length === 0) {
            delete state.frontendToolNames[name];
          }
        }
        break;
      case "agenticAi/registerContext":
        if (action.key in state.context) {
          throw new Error(`Context ${action.key} already registered`);
        }

        if (action.identifier in state.contextIdentifiers) {
          if (!action.options.ignoreDuplicates) {
            const error = new Error(`Context with identifier "${action.identifier}" already registered`);
            console.error(error);
            const existingFlags = state.contextIdentifiers[action.identifier].map(
              (key) => state.context[key]?.options.ignoreReactAppRootState
            );
            if (!isGlobalDuplicate(action.options.ignoreReactAppRootState, existingFlags)) {
              Sentry.captureException(error, {
                level: "error",
                fingerprint: duplicateRegistrationFingerprint("context", action.identifier),
              });
            }
          }
          state.contextIdentifiers[action.identifier].push(action.key);
        } else {
          state.contextIdentifiers[action.identifier] = [action.key];
        }

        state.context[action.key] = {
          value: action.value,
          identifier: action.identifier,
          options: action.options,
        };
        break;
      case "agenticAi/unregisterContext": {
        if (state.context[action.key]) {
          const identifier = state.context[action.key].identifier;
          delete state.context[action.key];

          state.contextIdentifiers[identifier] = state.contextIdentifiers[identifier].filter(
            (key) => key !== action.key
          );
          if (state.contextIdentifiers[identifier].length === 0) {
            delete state.contextIdentifiers[identifier];
          }
        }
        break;
      }
      case "agenticAi/registerLiquidField":
        // Upsert: update if exists, insert if not
        state.liquidFields[action.key] = action.field;
        break;
      case "agenticAi/unregisterLiquidField":
        if (state.liquidFields[action.key]) {
          delete state.liquidFields[action.key];
        }
        break;
      case "agenticAi/clearSupportEntryPoint":
        state.supportEntryPoint = false;
        break;
      case "agenticAi/clearPendingExistingChatResponse":
        state.pendingExistingChatResponse = undefined;
        break;
      case "agenticAi/clearPendingBrandGuidelineContexts":
        // When the chat UI reports the specific IDs it just consumed, clear only
        // those — IDs queued by a concurrent openChat while consumption was in
        // flight must survive so they get attached on the follow-up run. With no
        // IDs (e.g. chat close, unsupported mode) drop the whole pending set.
        if (action.consumedIds?.length) {
          const consumed = new Set(action.consumedIds);
          const remaining = (state.pendingBrandGuidelineContextIds ?? []).filter((id) => !consumed.has(id));
          state.pendingBrandGuidelineContextIds = remaining.length > 0 ? remaining : undefined;
        } else {
          state.pendingBrandGuidelineContextIds = undefined;
        }
        break;
      case "agenticAi/registerAttachedFiles":
        if (action.key in state.attachedFiles) {
          throw new Error(`Attached files ${action.key} already registered`);
        }

        if (action.identifier in state.attachedFileIdentifiers) {
          // Why: two live registrations share an attached-files identifier,
          // which means a feature double-mounted its registration hook (or
          // copy-pasted another feature's identifier) and its files would ride
          // chat requests twice. Action: the identifier names the feature --
          // find its second registration site and fix the double mount or give
          // it its own identifier. Removable: never -- backstop against
          // arbitrary future registrants (mirrors registerContext).
          const error = new Error(`Attached files with identifier "${action.identifier}" already registered`);
          console.error(error);
          Sentry.captureException(error, {
            level: "error",
            fingerprint: duplicateRegistrationFingerprint("attached-files", action.identifier),
          });
          state.attachedFileIdentifiers[action.identifier].push(action.key);
        } else {
          state.attachedFileIdentifiers[action.identifier] = [action.key];
        }

        state.attachedFiles[action.key] = {
          identifier: action.identifier,
          files: action.files,
        };
        break;
      case "agenticAi/unregisterAttachedFiles":
        if (state.attachedFiles[action.key]) {
          const identifier = state.attachedFiles[action.key].identifier;
          delete state.attachedFiles[action.key];

          state.attachedFileIdentifiers[identifier] = state.attachedFileIdentifiers[identifier].filter(
            (key) => key !== action.key
          );
          if (state.attachedFileIdentifiers[identifier].length === 0) {
            delete state.attachedFileIdentifiers[identifier];
          }
        }
        break;
      case "agenticAi/registerSkill":
        if (action.key in state.skillDefinitions) {
          throw new Error(`Skill ${action.key} already registered`);
        }

        if (action.skill.name in state.registeredSkillNames) {
          const error = new Error(`Skill ${action.skill.name} already registered`);
          console.error(error);
          Sentry.captureException(error, {
            level: "error",
            fingerprint: duplicateRegistrationFingerprint("skill", action.skill.name),
          });
          state.registeredSkillNames[action.skill.name].push(action.key);
        } else {
          state.registeredSkillNames[action.skill.name] = [action.key];
        }

        state.skillDefinitions[action.key] = action.skill;
        break;
      case "agenticAi/unregisterSkill":
        if (state.skillDefinitions[action.key]) {
          const name = state.skillDefinitions[action.key].name;
          delete state.skillDefinitions[action.key];

          state.registeredSkillNames[name] = state.registeredSkillNames[name].filter((key) => key !== action.key);
          if (state.registeredSkillNames[name].length === 0) {
            delete state.registeredSkillNames[name];
            // Keep loadedSkillNames. registerSkill's identity follows the React-root
            // gate, so a Knockout handoff re-runs skill effects: they unregister and
            // register the same name under a new key. Dropping the loaded flag here
            // hides every held tool until the model calls load_skill again, and
            // nothing tells it to. A new session still clears the list.
          }
        }
        break;
      case "agenticAi/loadSkill":
        if (!state.loadedSkillNames.includes(action.name)) {
          state.loadedSkillNames.push(action.name);
        }
        break;
    }
  });
};
